import React from 'react';
import { SaleData } from '../../../types';
import { Product } from '../../../domain/entities';
import { CustomRow } from '../common/custom_row';
import { CustomInput } from '../common/custom_input';
import { CustomSelect } from '../common/custom_select';
import { CustomHintText } from '../common/custom_hint_text';
import { CustomButton } from '../common/custom_button';

interface SaleFormProps {
    saleData: SaleData;
    onOdometerChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onProductChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    onLitersChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onTotalValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onDriverRegistrationChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBack: () => void;
    onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    getPricePerLiter: () => string;
    isPriceMatch: () => boolean;
    isLoadingDriver: boolean;
    products: Product[];
    isLoadingProducts: boolean;
    isLoadingBalance: boolean;
    getFormattedBalanceLiters: () => string;
    getFormattedBalanceValue: () => string;
    isProcessingSale?: boolean;
}

export const SaleForm: React.FC<SaleFormProps> = ({
    saleData,
    onOdometerChange,
    onProductChange,
    onLitersChange,
    onTotalValueChange,
    onDriverRegistrationChange,
    onPasswordChange,
    onBack,
    onSubmit,
    getPricePerLiter,
    isPriceMatch,
    isLoadingDriver,
    products,
    isLoadingProducts,
    isLoadingBalance,
    getFormattedBalanceLiters,
    getFormattedBalanceValue,
    isProcessingSale = false
}) => {
    return (
        <form className="terminal-form" onSubmit={onSubmit} autoComplete="off">
            <CustomRow>
                <CustomInput
                    id="odometer"
                    label="Hodômetro:"
                    type="text"
                    value={saleData.odometer}
                    onChange={onOdometerChange}
                    placeholder="123456"
                    maxLength={8}
                    required
                />
                <CustomSelect
                    id="product"
                    label="Produto:"
                    value={saleData.productId?.toString() || ''}
                    onChange={onProductChange}
                    required={true}
                    disabled={isLoadingProducts} >
                    <option value="">{isLoadingProducts ? 'Carregando produtos...' : 'Selecione o produto'}</option>
                    {products.map(product => (
                        <option key={product.id} value={product.id.toString()}>
                            {product.name}
                        </option>
                    ))}
                </CustomSelect>
            </CustomRow>
            <CustomRow>
                <div className="form-group main-field">
                    <label htmlFor="liters">Quantidade (Litros):</label>
                    <input
                        type="text"
                        id="liters"
                        value={saleData.liters}
                        onChange={onLitersChange}
                        placeholder="0,00"
                        maxLength={12}
                        required={true}
                        autoComplete="off"
                        data-lpignore="true"
                        data-form-type="other"
                        inputMode="numeric"
                    />
                    <div className="price-info-row">
                        <div className="price-info-item">
                            <span className="price-label">Valor por Litro:</span>
                            <span className="price-value">R$ {getPricePerLiter()}/L</span>
                        </div>
                        {saleData.basePrice && <div className={`price-info-item ${isPriceMatch() ? 'base-price-match' : 'base-price-different'}`}>
                            <span className="price-label">Preço Base:</span>
                            <span className="price-value">R$ {saleData.basePrice ? saleData.basePrice.toFixed(2) : ''}/L</span>
                        </div>}
                    </div>
                </div>
                <div className="form-group main-field">
                    <label htmlFor="totalValue">Valor Total:</label>
                    <input
                        type="text"
                        id="totalValue"
                        value={saleData.totalValue}
                        onChange={onTotalValueChange}
                        placeholder="0,00"
                        maxLength={12}
                        required={true}
                        autoComplete="off"
                        data-lpignore="true"
                        data-form-type="other"
                        inputMode="numeric"
                    />
                    <div className="balance-info-row">
                        <div className="balance-info-item">
                            <span className="balance-label">Saldo Litros:</span>
                            <span className="balance-value">
                                {isLoadingBalance ? (
                                    <span className="loading-indicator">
                                        <span className="loading-spinner-small"></span>
                                        Carregando...
                                    </span>
                                ) : (
                                    getFormattedBalanceLiters()
                                )}
                            </span>
                        </div>
                        <div className="balance-info-item">
                            <span className="balance-label">Saldo Valor:</span>
                            <span className="balance-value">
                                {isLoadingBalance ? (
                                    <span className="loading-indicator">
                                        <span className="loading-spinner-small"></span>
                                        Carregando...
                                    </span>
                                ) : (
                                    getFormattedBalanceValue()
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </CustomRow>
            <CustomRow>
                <CustomInput
                    id="driverRegistration"
                    label="Matrícula do Motorista:"
                    type="text"
                    value={saleData.driverRegistration}
                    onChange={onDriverRegistrationChange}
                    placeholder="1234567890"
                    maxLength={10}
                    required={true} />
                <CustomHintText hintText={isLoadingDriver ? 'Carregando motorista...' : saleData.driverName} />
            </CustomRow>
            <CustomRow>
                <CustomInput
                    id="password"
                    label="Senha:"
                    type="password"
                    value={saleData.password}
                    onChange={onPasswordChange}
                    placeholder="123456"
                    maxLength={6}
                    required={true}
                    autoComplete="off"
                    dataLpIgnore={true}
                    dataFormType="other" />
            </CustomRow>

            <div className="button-group">
                <CustomButton
                    title="Voltar"
                    type="button"
                    className="back-btn"
                    onClick={onBack}
                    disabled={isProcessingSale}
                />
                <CustomButton
                    title={isProcessingSale ? 'Processando...' : 'Processar Venda'}
                    type="submit"
                    className="submit-btn"
                    disabled={isProcessingSale}
                />
            </div>
        </form>
    );
};
